import { Alert, Paper, Stack, Text } from "@mantine/core";
import { skipToken } from "@reduxjs/toolkit/query";
import React from "react";
import { useGetMcbimFloorsQuery, useGetMcbimSpacesQuery } from "../../../../../../openapi";
import { selectCurrentProjectId } from "../../../../../global/globalSliceCommon";
import { useAppDispatch, useAppSelector } from "../../../../../hooks";
import { selectProjectNo } from "../../../../projectSelectors";
import { useGetMcBimMongoFilesStatsQuery } from "../../../admin/mcbim/McBimMongoRTK";
import FloorplanFloorList from "./FloorplanFloorList";
import FloorplanViewer from "./FloorplanViewer";
import { selectMcbimFloorsArgs, selectSelectedFloor, selectUniqueMcbimFloors } from "./floorplanSelectors";
import { setSelectedFloorInRedux } from "./floorplanSlice";
import {
  selectMcbimHasNoSpaces,
  selectMcbimHasNoSyncedFiles,
  selectMcbimIsActivated,
  selectMcbimMongoFilesStatsArgs,
} from "./mcbimSelectors";
import McbimFloorplanInfo from "./McbimFloorplanInfo";
import { selectIssueBoardId } from "../../redux/selectors/issueCommonSelectors";

const IssueFloorplan: React.FC = () => {
  const dispatch = useAppDispatch();
  const projectId = useAppSelector(selectCurrentProjectId) as string;
  const issueBoardId = useAppSelector(selectIssueBoardId);
  const projectNo = useAppSelector(selectProjectNo);
  const floors = useAppSelector(selectUniqueMcbimFloors);
  const selectedFloor = useAppSelector(selectSelectedFloor);
  const mcbimFloorsArgs = useAppSelector(selectMcbimFloorsArgs);
  const mcbimIsActivated = useAppSelector(selectMcbimIsActivated);
  const mcbimMongoFilesArgs = useAppSelector(selectMcbimMongoFilesStatsArgs);
  const mcbimHasNoSyncedFiles = useAppSelector(selectMcbimHasNoSyncedFiles);
  const mcbimHasNoSpaces = useAppSelector(selectMcbimHasNoSpaces);
  const { data: mcbimSpaces = [], isError } = useGetMcbimSpacesQuery(
    { projectNo: projectNo as number },
    { skip: projectNo == null || !mcbimIsActivated },
  );
  const { isFetching: floorsFetching, isError: floorsIsError } = useGetMcbimFloorsQuery(
    mcbimIsActivated && mcbimFloorsArgs != null ? mcbimFloorsArgs : skipToken,
  );
  useGetMcBimMongoFilesStatsQuery(mcbimMongoFilesArgs ?? { projectId: "" }, {
    skip: mcbimMongoFilesArgs == null,
    refetchOnMountOrArgChange: true,
  });

  React.useEffect(() => {
    if (issueBoardId == null || selectedFloor !== "" || floors.length === 0) {
      return;
    }

    dispatch(setSelectedFloorInRedux({ issueBoardId, selectedFloor: floors[0] }));
  }, [dispatch, floors, issueBoardId, selectedFloor]);

  return (
    <Stack gap="md" p="sm" style={{ width: "100%", height: "100%", minHeight: 0, flex: 1 }}>
      {!mcbimIsActivated && <McbimFloorplanInfo projectId={projectId} variant="notActivated" />}
      {mcbimHasNoSyncedFiles && <McbimFloorplanInfo projectId={projectId} variant="noFiles" />}
      {mcbimHasNoSpaces && <McbimFloorplanInfo projectId={projectId} variant="noSpaces" />}
      {mcbimIsActivated && isError && <Alert color="red">Kunne ikke hente romdata fra MCBIM.</Alert>}
      {mcbimIsActivated && floorsIsError && <Alert color="red">Kunne ikke hente etasjer fra MCBIM.</Alert>}
      {mcbimIsActivated &&
        !mcbimHasNoSyncedFiles &&
        !isError &&
        !floorsFetching &&
        mcbimSpaces.length > 0 &&
        floors.length === 0 && (
          <Paper withBorder p="lg" radius="md">
            <Text size="sm" c="dimmed">
              Ingen etasjer tilgjengelig ennå.
            </Text>
          </Paper>
        )}
      {mcbimIsActivated && !mcbimHasNoSyncedFiles && floors.length > 0 && (
        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: "280px minmax(0, 1fr)",
            gap: "1rem",
          }}
        >
          <FloorplanFloorList />
          <FloorplanViewer />
        </div>
      )}
    </Stack>
  );
};

export default IssueFloorplan;
